import { apiClient } from './apiClient';

import type {
  ApiAvailableSlot,
  ApiBookingData,
  ApiBookingTokenContext,
  BookingConfirmation,
  BookingSlot,
  BookingTokenData,
} from '@/types/scheduling.types';
import type { ApiRequestOptions } from '@kent/shared';

// ── Helpers ──────────────────────────────────────────────────────────

interface ApiEnvelope<T> {
  code: number;
  status: string;
  message: string;
  data: T;
}

function bookingTokenOptions(token: string): ApiRequestOptions {
  return { headers: { 'x-booking-token': token }, skipAuth: true };
}

function mapAvailableSlot(slot: ApiAvailableSlot): BookingSlot {
  return {
    slotId: slot.slotId,
    startAtUtc: slot.startAtUtc,
    endAtUtc: slot.endAtUtc,
    availableSpots: slot.availableCapacity,
  };
}

function mapBookingToConfirmation(booking: ApiBookingData): BookingConfirmation {
  const interviewer = booking.slot?.interviewer;
  const interviewerName = interviewer ? `${interviewer.firstName} ${interviewer.lastName}` : '';

  return {
    bookingId: booking.bookingId,
    slotId: booking.slotId,
    startAtUtc: booking.slot?.startAtUtc ?? '',
    endAtUtc: booking.slot?.endAtUtc ?? '',
    interviewerName,
    status: booking.status,
  };
}

// ── Public API ───────────────────────────────────────────────────────

export async function validateToken(token: string): Promise<BookingTokenData> {
  const contextRes = await apiClient.get<ApiEnvelope<ApiBookingTokenContext>>(
    `/scheduling/bookings/by-token/${encodeURIComponent(token)}`,
    bookingTokenOptions(token),
  );

  const slotsRes = await apiClient.get<ApiEnvelope<ApiAvailableSlot[]>>(
    '/scheduling/bookings/available-slots',
    bookingTokenOptions(token),
  );

  return {
    applicationId: contextRes.data.applicationId,
    applicantName: contextRes.data.applicantName,
    jobTitle: contextRes.data.jobPostingTitle ?? '',
    slots: slotsRes.data.map(mapAvailableSlot),
  };
}

export async function createBooking(token: string, slotId: string): Promise<BookingConfirmation> {
  const res = await apiClient.post<ApiEnvelope<ApiBookingData>>(
    '/scheduling/bookings',
    { slotId },
    bookingTokenOptions(token),
  );

  return mapBookingToConfirmation(res.data);
}

export async function rescheduleBooking(
  bookingId: string,
  token: string,
  newSlotId: string,
  notes?: string,
): Promise<BookingConfirmation> {
  const res = await apiClient.put<ApiEnvelope<ApiBookingData>>(
    `/scheduling/bookings/${encodeURIComponent(bookingId)}`,
    { newSlotId, notes },
    bookingTokenOptions(token),
  );

  return mapBookingToConfirmation(res.data);
}

export async function cancelBooking(bookingId: string, token: string, reason?: string): Promise<void> {
  await apiClient.delete<ApiEnvelope<null>>(
    `/scheduling/bookings/${encodeURIComponent(bookingId)}${reason ? `?reason=${encodeURIComponent(reason)}` : ''}`,
    bookingTokenOptions(token),
  );
}

export async function downloadIcs(bookingId: string, token: string): Promise<Blob> {
  const response = await fetch(`/api/scheduling/bookings/${encodeURIComponent(bookingId)}/ics`, {
    headers: { 'x-booking-token': token },
  });

  if (!response.ok) {
    throw new Error('Failed to download ICS file');
  }

  return response.blob();
}
